import { MAX_PHOTO_BYTES } from '../audit/attachmentRules';

/**
 * Client-side photo compression (spec §8): downscale + JPEG re-encode until
 * the result fits the ≤500KB budget. Runs only in the browser (canvas);
 * logic is deliberately simple — modern phone photos compress to well under
 * 500KB at 1600px / q0.7.
 */

const START_DIMENSION = 1600;
const MIN_DIMENSION = 640;
const QUALITY_STEPS = [0.8, 0.65, 0.5];

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('JPEG encoding failed'))),
      'image/jpeg',
      quality,
    );
  });
}

async function decode(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    throw new Error(
      'This image format is not supported by your browser — try a JPEG or PNG.',
    );
  }
}

/** Compress an image file to ≤ maxBytes; throws only on undecodable input. */
export async function compressPhoto(
  file: Blob,
  maxBytes: number = MAX_PHOTO_BYTES,
): Promise<Blob> {
  const bitmap = await decode(file);
  try {
    let dimension = Math.min(START_DIMENSION, Math.max(bitmap.width, bitmap.height));
    for (;;) {
      const scale = dimension / Math.max(bitmap.width, bitmap.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * Math.min(scale, 1)));
      canvas.height = Math.max(1, Math.round(bitmap.height * Math.min(scale, 1)));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas unavailable — cannot compress the photo.');
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      for (const quality of QUALITY_STEPS) {
        const blob = await canvasToJpeg(canvas, quality);
        if (blob.size <= maxBytes) return blob;
      }
      if (dimension <= MIN_DIMENSION) {
        // Accept the smallest attempt rather than fail — a 640px q0.5 JPEG
        // over 500KB is pathological, but a slightly-over photo beats none.
        return canvasToJpeg(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]!);
      }
      dimension = Math.round(dimension * 0.7);
    }
  } finally {
    bitmap.close();
  }
}
